// Maps the password sign-in answer (api-surface 1.4) to a door message.
// 200 is handled by the caller (auth/me refetch); 409 and 428 are not
// failures at all but the two access challenges, so they carry the challenge
// they open rather than an apology. Everything else is a stated reason and
// next step. Never the raw JSON.
import { isApiError } from '../../api/errors';
import { stateOfError, type HonestDescriptor } from '../../states/stateOf';

export type LoginNext = 'retry' | 'verify' | 'challenge_workspace' | 'challenge_password' | 'wait';

export interface LoginOutcome extends HonestDescriptor {
  readonly next: LoginNext;
}

// The two challenge outcomes the door hands to the access-challenge screen.
export function challengeKindOf(next: LoginNext): 'workspace' | 'password' | null {
  if (next === 'challenge_workspace') return 'workspace';
  if (next === 'challenge_password') return 'password';
  return null;
}

export function loginOutcomeOf(error: unknown): LoginOutcome {
  if (isApiError(error)) {
    if (error.status === 401) return { variant: 'Authentication required', reason: 'The identifier or password did not match.', next: 'retry' };
    if (error.status === 403 && error.code === 'email_verification_required') {
      return { variant: 'Authentication required', reason: 'This account still needs its email verified. Open the verification link from the invitation email.', next: 'verify' };
    }
    if (error.status === 409 && error.code === 'tenant_selection_required') {
      return { variant: 'Authentication required', reason: 'This identity belongs to more than one workspace. Choose the one to open.', next: 'challenge_workspace' };
    }
    if (error.status === 428) {
      return { variant: 'Authentication required', reason: 'The temporary owner password must be replaced before the workspace opens.', next: 'challenge_password' };
    }
    if (error.status === 429) return { variant: 'Rate limited', reason: 'Too many sign-in attempts. The limit is 5 per 15 minutes.', next: 'wait' };
  }
  const mapped = stateOfError(error);
  return { ...mapped, next: mapped.variant === 'Provider unavailable' ? 'wait' : 'retry' };
}
